import { Music2 } from "lucide-react";
import { useState } from "react";
import type { Locale } from "@vzlive/shared";
import { t } from "@vzlive/shared/i18n";

interface SetlistPayload {
  title: string;
  updatedAt: string;
  songs: Array<{
    id: string;
    position: number;
    title: string;
    note?: string | null;
  }>;
}

/**
 * Setlist als horizontal scrollende Song-Leiste über dem Ticker. Der
 * zuletzt gespielte Song ist hervorgehoben. Der Umschalter am Ende der
 * Leiste klappt die vollständige Songliste auf. Ersetzt die
 * aufklappbare Sidebar-Setlist.
 */
export function SetlistStrip({
  payload,
  locale,
}: {
  payload: unknown;
  locale?: Locale;
}) {
  const [open, setOpen] = useState(false);
  const value = payload as SetlistPayload;
  if (!value?.songs?.length) return null;
  const label = value.title || "Setlist";
  return (
    <div
      className={`pulse-setlistwrap${open ? " pulse-setlistwrap--open" : ""}`}
    >
      <nav className="pulse-setlist" aria-label={label}>
        {value.songs.map((song) => {
          const now = String(song.position) === String(value.songs.length);
          return (
            <span
              key={song.id}
              className={`pulse-setlist__chip${
                now ? " pulse-setlist__chip--now" : ""
              }`}
              aria-current={now ? "true" : undefined}
            >
              {String(song.position).padStart(2, "0")} · <b>{song.title}</b>
              {song.note && <em> · {song.note}</em>}
            </span>
          );
        })}
        {locale && (
          <button
            type="button"
            className="pulse-setlist__chip pulse-setlist__toggle"
            aria-expanded={open}
            onClick={() => setOpen((current) => !current)}
          >
            {t(locale, "setlistAll")}
          </button>
        )}
      </nav>
      <div id="pulse-setlistfull" className="pulse-setlistfull">
        <ol>
          {value.songs.map((song, index) => {
            const now = index === value.songs.length - 1;
            return (
              <li key={song.id} aria-current={now ? "true" : undefined}>
                <i>{String(song.position).padStart(2, "0")}</i>
                <b>{song.title}</b>
                {song.note && <em> · {song.note}</em>}
              </li>
            );
          })}
        </ol>
      </div>
    </div>
  );
}

/** legacy list variant */
export function Setlist({ payload }: { payload: unknown }) {
  const value = payload as SetlistPayload;
  if (!value?.songs?.length) return null;
  return (
    <section className="setlist-section">
      <header>
        <span>
          <Music2 size={18} aria-hidden="true" />
        </span>
        <div>
          <h2>{value.title || "Setlist"}</h2>
        </div>
      </header>
      <ol>
        {value.songs.map((song) => (
          <li key={song.id}>
            <span>{String(song.position).padStart(2, "0")}</span>
            <strong>{song.title}</strong>
            {song.note && <small>{song.note}</small>}
          </li>
        ))}
      </ol>
    </section>
  );
}
